<script setup lang="ts">
import type { BusinessType, Country, CreateBusiness, Response } from "~/types";

definePageMeta({ layout: "none", pageTransition: false });

const { t } = useI18n();

useHead({ titleTemplate: (chunk) => chunk || 'bookforme', title: () => seoTitle(t, 'page.create_business.page_title', 'Request a Demo') });
const { apiVersion } = useRuntimeConfig().public;
const appConfig = useAppConfig();
const localePath = useLocalePath();
const toast = useToast();
const { createBusinessDetailsRequest, createYourAccountRequest } =
  useValidation();

const { createBusiness, storeBusiness } = usePostRequests();

const form = ref();
const currentStep = ref(1);
const businessTypes = ref<BusinessType[]>([]);
const countries = ref<Country[]>([]);
const selectedCountry = ref<Country | undefined>(undefined);

const createBusinessFormData = ref<CreateBusiness>({
  business_type: null,
  business_name: "",
  business_email: "",
  country_id: undefined,
  first_name: "",
  last_name: "",
  mobile: "",
  step: 1,
});

watch(
  () => selectedCountry.value,
  () => {
    if (selectedCountry?.value)
      createBusinessFormData.value.country_id = selectedCountry?.value?.id;
  },
);

const { submit, inProgress } = useSubmit(
  () => {
    createBusinessFormData.value.step = currentStep?.value + 1;
    if (selectedCountry?.value)
      createBusinessFormData.value.country_id = selectedCountry?.value?.id;
    if (currentStep?.value !== 3) {
      return createBusiness(createBusinessFormData?.value);
    } else {
      return storeBusiness(createBusinessFormData?.value);
    }
  },
  {
    onError: () => backStep,
  },
);

const backStep = () => (currentStep.value -= 1);

const nextStep = async () => {
  try {
    const validateAndSubmit = async () => {
      const isValid = await form?.value?.validate();
      if (!isValid) return false;
      const response = await submit();
      if (response) currentStep.value += 1;
    };

    switch (currentStep.value) {
      case 1:
        if (createBusinessFormData?.value?.business_type) {
          // `submit()` resolves to undefined when the request fails, instead of
          // throwing — advancing on the await alone would move past a failed step.
          const response = await submit();
          if (response) currentStep.value += 1;
        } else {
          toast.add({
            title: "Please select a business type",
            color: "red",
            icon: "i-approved-warning",
          });
        }
        break;
      case 2:
        if (selectedCountry?.value)
          createBusinessFormData.value.country_id = selectedCountry?.value?.id;
        await validateAndSubmit();
        break;
      case 3:
        await validateAndSubmit();
        break;
      default:
        break;
    }
  } catch (error) {
    console.error("Validation Error", error);
  }
};

appConfig.ui.primary = "blue";

const { data: response, error } = await useLarafetch<
  Response<{
    countries: Country[];
    business_types: BusinessType[];
  }>
>(`/api/${apiVersion}/request-demo`);

/**
 * Step one is "pick your business type", so the wizard cannot be used at all
 * without this payload. It used to be read behind a bare `if (response.value)`
 * with no `else`: a failed request left every list empty and still rendered
 * the wizard, so the visitor got step one with nothing to choose, no error and
 * no way forward. An empty list is the same dead end, so it is handled here
 * too rather than only the failed request.
 */
if (error.value || !response.value?.data?.business_types?.length) {
  showError({ statusCode: httpErrorStatus(error.value) ?? 503 });
}
else {
  businessTypes.value = response.value.data.business_types;
  countries.value = response.value.data?.countries ?? [];
  selectedCountry.value = countries.value.find((country) => country.selected);
}
</script>

<template>
  <div class="flex flex-1 lg:flex-row flex-col lg:h-screen">
    <CreateBusinessAside v-model:current-step="currentStep" />
    <div
      class="flex-1 min-h-0 h-full grid justify-center items-center p-8 sm:p-10 md:p-12 lg:p-15 overflow-y-auto overflow-x-hidden"
    >
      <UForm
        ref="form"
        class="space-y-10 w-full mx-auto"
        :state="createBusinessFormData"
        :schema="
          currentStep === 2
            ? createBusinessDetailsRequest
            : createYourAccountRequest
        "
        @submit="nextStep"
      >
        <div
          class="flex-1 w-full max-w-135 mx-auto h-full flex flex-col justify-center items-center"
        >
          <CreateBusinessStepOne
            v-if="currentStep === 1"
            v-model:selected-business-type="
              createBusinessFormData.business_type
            "
            :business-types="businessTypes"
          />
          <CreateBusinessStepTwo
            v-else-if="currentStep === 2"
            v-model:selected-country="selectedCountry"
            v-model:business-name="createBusinessFormData.business_name"
            :countries="countries"
          />
          <CreateBusinessStepThree
            v-else-if="currentStep === 3"
            v-model:selected-country="selectedCountry"
            v-model:first-name="createBusinessFormData.first_name"
            v-model:last-name="createBusinessFormData.last_name"
            v-model:business-email="createBusinessFormData.business_email"
            v-model:mobile="createBusinessFormData.mobile"
          />
          <CreateBusinessStepFour v-else-if="currentStep === 4" />
        </div>

        <div
          v-if="currentStep <= 3"
          class="flex-1 flex justify-between space-x-2 rtl:space-x-reverse"
        >
          <UButton
            v-if="currentStep > 1"
            type="button"
            :disabled="inProgress"
            :label="
              currentStep === 2
                ? t(
                    'page.create_business.business_details.back_to_business_type',
                  )
                : t(
                    'page.create_business.business_owner.back_to_business_details',
                  )
            "
            size="xl"
            color="white"
            class="flex-1 lg:max-w-fit py-3 rounded-xl"
            block
            @click="backStep"
          />
          <div class="flex-1 flex justify-end">
            <UButton
              type="submit"
              :loading="inProgress"
              :disabled="!createBusinessFormData?.business_type"
              :label="
                currentStep === 3 ? t('label.submit') : t('label.next_step')
              "
              size="xl"
              color="blue"
              class="flex-1 lg:max-w-40 py-3 rounded-xl"
              block
              @click="nextStep"
            />
          </div>
        </div>
        <div
          v-else
          class="flex-1 flex justify-center space-x-2 rtl:space-x-reverse"
        >
          <UButton
            type="button"
            :label="t('label.home')"
            size="xl"
            color="white"
            class="w-40 py-3 rounded-xl"
            block
            @click="() => navigateTo(localePath('/'))"
          />
          <UButton
            type="button"
            :label="t('label.contact_us')"
            size="xl"
            color="blue"
            class="w-40 py-3 rounded-xl"
            block
            @click="() => navigateTo(localePath('/contact-us'))"
          />
        </div>
      </UForm>
    </div>
  </div>
</template>
